import api from './api';

// Disk usage thresholds — must match backend constants in
// backend/app/services/onprem/beacon_service.py:
//   DISK_WARNING_PERCENT = 85.0
//   DISK_CRITICAL_PERCENT = 95.0
// Both backend and frontend use STRICT greater-than (`>`) for the
// boundary so a reading of exactly 85.0 or 95.0 stays in the lower
// severity bucket — the backend will not alert on it, the frontend
// will not flag it red.
export const DISK_WARNING_PERCENT = 85;
export const DISK_CRITICAL_PERCENT = 95;

export interface InstallSummary {
  install_id: string;
  client_id: string;
  license_id: string;
  last_beacon_at: string; // ISO 8601
  minutes_since_last_beacon: number;
  host_os: string | null;
  disk_percent_used: number | null;
  backend_up: boolean;
  db_up: boolean;
  agents_configured: number;
  agents_connected: number;
  fleet_manager_version: string | null;
  total_beacons: number;
}

export interface BeaconDetail {
  id: string;
  received_at: string; // ISO 8601 UTC
  host_os: string | null;
  disk_percent_used: number | null;
  backend_up: boolean;
  db_up: boolean;
  agents_configured: number;
  agents_connected: number;
  jobs_last_24h: number;
  errors_last_24h: number;
  fleet_manager_version: string | null;
  beacon_version: string | null;
}

export const listInstalls = async (
  limit = 200,
  signal?: AbortSignal,
): Promise<InstallSummary[]> => {
  const response = await api.get<InstallSummary[]>('/onprem-admin/installs', {
    params: { limit },
    signal,
  });
  return response.data;
};

export const getInstallBeacons = async (
  installId: string,
  limit = 50,
  signal?: AbortSignal,
): Promise<BeaconDetail[]> => {
  const response = await api.get<BeaconDetail[]>(
    `/onprem-admin/installs/${installId}/beacons`,
    { params: { limit }, signal },
  );
  return response.data;
};

// ── Licenses ──────────────────────────────────────────────────────────────────
// Mirrors backend/app/schemas/onprem.py (LicenseCreateRequest / LicenseResponse).

export type LicenseStatus = 'unused' | 'active' | 'suspended' | 'revoked';

export interface LicenseCreateRequest {
  client_id: string;
  organization_name: string;
  plan: string;
  max_devices: number;
  max_users: number;
  features?: string[];
  term_months?: number;
  notes?: string | null;
}

export interface License {
  id: string;
  license_key: string;
  client_id: string;
  organization_name: string;
  plan: string;
  max_devices: number;
  max_users: number;
  features: string[];
  status: LicenseStatus | string;
  install_fingerprint?: string | null;
  install_id?: string | null;
  activated_at?: string | null;
  term_months: number;
  expires_at: string;
  created_at: string;
  notes?: string | null;
}

export const listLicenses = async (
  limit = 200,
  signal?: AbortSignal,
): Promise<License[]> => {
  const response = await api.get<License[]>('/onprem-admin/licenses', {
    params: { limit },
    signal,
  });
  return response.data;
};

export const createLicense = async (body: LicenseCreateRequest): Promise<License> => {
  const response = await api.post<License>('/onprem-admin/licenses', body);
  return response.data;
};

export const revokeLicense = async (licenseId: string, reason: string): Promise<License> => {
  const response = await api.post<License>(`/onprem-admin/licenses/${licenseId}/revoke`, {
    reason,
  });
  return response.data;
};

export const updateLicenseNotes = async (
  licenseId: string,
  notes: string | null,
): Promise<License> => {
  const response = await api.patch<License>(`/onprem-admin/licenses/${licenseId}`, { notes });
  return response.data;
};
